import {
  ActionIcon,
  Box,
  Grid,
  Group,
  List,
  ScrollArea,
  Slider,
  Switch,
  ThemeIcon,
  Text,
  LoadingOverlay,
} from '@mantine/core'
import { GeoJson, GeoJsonFeature } from 'pigeon-maps'
import { FC, useMemo, useState, useEffect } from 'react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { useResizeObserver, useValidatedState } from '@mantine/hooks'
import MapViewer from '@/components/MapViewer'
import classes from './RouteStatistics.module.css'
import { clsx } from 'clsx'

import SVGOverlay from './SVGOverlay'
import { getPlanningDataOptions } from '@/api/main'
import { DetailedRouteStatistics } from '@/api/main/types.gen'
import { TbCircleDot } from 'react-icons/tb'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { multiPolygonToGeoJsonPolygons } from '@/utils/geojson'
import { useTranslation } from 'react-i18next'

type GeoJSON = {
  coordinates: [[number, number]]
}

const RouteStatistics: FC<{
  routeId: string
  routeVersion: string
  planningAreaId?: string
  routeStatistics: DetailedRouteStatistics
  selectedFragment: number
  onSelectedFragmentChange: (value: number) => void
}> = ({ routeId, routeVersion: route_version, planningAreaId, routeStatistics, selectedFragment }) => {
  const { t } = useTranslation('routeManagement')
  const [mapRef, mapRect] = useResizeObserver()
  const [displayParkingPolygons, setDisplayParkingPolygons] = useState(false)
  const [showPolygonLoader, setShowPolygonLoader] = useState(false)

  const navigate = useNavigate()
  const { route_plan, fragments, street_segments, existing_versions } = routeStatistics

  const [{ lastValidValue: highlightedSegment }, setHighlightedSegment] = useValidatedState(
    0,
    value => value >= 0 && value < route_plan.length
  )

  const highlightedSegmentIds = useMemo(() => {
    const ids = []
    for (let i = 0; i < 3; i++) {
      const segment = route_plan[highlightedSegment! + i]
      if (segment) {
        ids.push(segment.street_segment_id)
      }
    }
    return ids
  }, [route_plan, highlightedSegment])

  const firstCoord = (Object.values(street_segments).values().next()?.value?.segment_geojson as GeoJSON)
    ?.coordinates?.[0]

  const planningAreaIdForQuery = planningAreaId ?? '__missing__'
  const { data: planningData, isLoading: isLoadingPlanningData } = useQuery({
    ...getPlanningDataOptions({
      path: { area_id: planningAreaIdForQuery },
      query: { include_within: 0 },
      headers: { 'city-sense-control-data-schema': 'control_data_next' },
    }),
    enabled: displayParkingPolygons && Boolean(planningAreaId),
  })

  const parkingPolygonFeatures = useMemo(() => {
    if (!planningData) {
      return null
    }

    return planningData.technical_segments.flatMap(segment =>
      multiPolygonToGeoJsonPolygons(segment.geom).map((geometry, polyIndex) => (
        <GeoJsonFeature
          key={`${segment.technical_segment_id}-${polyIndex}`}
          svgAttributes={{
            style: {
              fill: 'rgba(205, 121, 244, 0.77)',
              stroke: 'rgba(212, 0, 255, 0.9)',
              strokeWidth: 1,
              pointerEvents: 'none',
            },
          }}
          feature={{
            type: 'Feature',
            geometry,
          }}
        />
      ))
    )
  }, [planningData])

  useEffect(() => {
    if (displayParkingPolygons && (isLoadingPlanningData || (planningData && !parkingPolygonFeatures))) {
      const timeoutId = setTimeout(() => {
        setShowPolygonLoader(true)
      }, 300)

      return () => {
        clearTimeout(timeoutId)
      }
    } else {
      setShowPolygonLoader(false)
    }
  }, [displayParkingPolygons, isLoadingPlanningData, planningData, parkingPolygonFeatures])

  return (
    <>
      <Grid>
        <Grid.Col span={11}>
          <Group mb="xs">
            <Switch
              label={t('displayParkingPolygon')}
              checked={displayParkingPolygons}
              onChange={event => setDisplayParkingPolygons(event.currentTarget.checked)}
            />
          </Group>
          <Box pos="relative">
            <MapViewer
              containerRef={mapRef}
              className={classes.map}
              center={{ latitude: firstCoord[1], longitude: firstCoord[0] }}
              zoom={14}
              overlays={[
                displayParkingPolygons && parkingPolygonFeatures ? (
                  <GeoJson key="parkingPolygons">{parkingPolygonFeatures}</GeoJson>
                ) : null,
                <GeoJson key="routePlan">
                  {Object.values(street_segments).map(
                    ({ street_segment_id, requested_passes, requested_reverse_passes }) => {
                      const routePlanPositions = route_plan
                        .map(({ street_segment_id }, i) => [i, street_segment_id] as const)
                        .filter(([_, id]) => id === street_segment_id)
                        .map(([i, _]) => i)

                      return (
                        <GeoJsonFeature
                          key={street_segment_id}
                          svgAttributes={{
                            className: clsx({
                              [classes.routePlanItem]: true,
                              [classes.inactiveRoutePlanItem]: routePlanPositions.every(
                                i =>
                                  i < fragments[selectedFragment]?.start_index ||
                                  i >= fragments[selectedFragment]?.end_index
                              ),
                              [classes.notRequiredSegment]: Math.max(requested_passes, requested_reverse_passes) === 0,
                              [classes.singlePassSegment]: Math.max(requested_passes, requested_reverse_passes) === 1,
                              [classes.doublePassSegment]: Math.max(requested_passes, requested_reverse_passes) === 2,
                            }),
                            style: {
                              ...(highlightedSegmentIds[0] === street_segment_id && {
                                stroke: 'var(--mantine-color-violet-5)',
                                strokeWidth: 5,
                                strokeDasharray: '1 0',
                              }),
                              ...(highlightedSegmentIds[1] === street_segment_id && {
                                stroke: 'var(--mantine-color-red-6)',
                                strokeWidth: 5,
                                strokeDasharray: '1 0',
                              }),
                              ...(highlightedSegmentIds[2] === street_segment_id && {
                                stroke: 'var(--mantine-color-yellow-6)',
                                strokeWidth: 5,
                                strokeDasharray: '1 0',
                              }),
                            },
                          }}
                          feature={{
                            type: 'Feature',
                            geometry: street_segments[street_segment_id]!.segment_geojson,
                          }}
                        />
                      )
                    }
                  )}
                </GeoJson>,
                <SVGOverlay key="fragmentEndPoints">
                  {scale => {
                    const { start_point, end_point } = fragments[selectedFragment]
                    const [startX, startY] = scale([start_point.latitude, start_point.longitude])
                    const [endX, endY] = scale([end_point.latitude, end_point.longitude])

                    return (
                      <>
                        <circle cx={startX} cy={startY} r={10} className={classes.startPoint} />
                        <circle cx={endX} cy={endY} r={10} className={classes.endPoint} />
                      </>
                    )
                  }}
                </SVGOverlay>,
              ]}
            />
            <LoadingOverlay
              visible={showPolygonLoader}
              zIndex={1000}
              overlayProps={{ radius: 'sm', blur: 2 }}
              loaderProps={{ children: 'Loading parking polygons...' }}
            />
          </Box>
          <Group justify="space-between">
            <Slider
              w={'calc(100% - 80px)'}
              value={highlightedSegment}
              onChange={setHighlightedSegment}
              min={0}
              max={route_plan.length - 1}
              label={index => `${index}: ${street_segments[route_plan[index].street_segment_id]!.street_name}`}
              marks={[
                {
                  value: fragments[selectedFragment]?.start_index,
                  label: t('fragmentStart'),
                },
                {
                  value: fragments[selectedFragment]?.end_index - 1,
                  label: t('fragmentEnd'),
                },
              ]}
              styles={{
                track: {
                  backgroundColor: 'var(--mantine-color-gray-6)',
                },
                markLabel: {
                  marginLeft: 40,
                  marginTop: 10,
                },
              }}
            />

            <Group gap={8}>
              <ActionIcon
                onClick={() => setHighlightedSegment(highlightedSegment! - 1)}
                disabled={highlightedSegment! <= 0}
              >
                <FaChevronLeft />
              </ActionIcon>
              <ActionIcon
                onClick={() => setHighlightedSegment(highlightedSegment! + 1)}
                disabled={highlightedSegment! + 1 >= route_plan.length}
              >
                <FaChevronRight />
              </ActionIcon>
            </Group>
          </Group>
        </Grid.Col>
        <Grid.Col span={1}>
          <Text fw={500}>{t('selectVersion')}</Text>
          <ScrollArea h={mapRect?.height ?? 790}>
            <List
              spacing="xs"
              size="sm"
              center
              icon={
                <ThemeIcon size={24} radius="xl" color="var(--mantine-color-blue-6)">
                  <TbCircleDot size={16} />
                </ThemeIcon>
              }
            >
              {[...existing_versions]
                .sort((a, b) => b.localeCompare(a))
                .map(version => (
                  <List.Item
                    key={version}
                    title={version}
                    onClick={() => {
                      navigate({ to: '/route/$routeId/$version', params: { routeId, version } })
                    }}
                    className={clsx({
                      [classes.versionItem]: true,
                      [classes.activeVersion]: version === route_version,
                    })}
                  >
                    {version}
                  </List.Item>
                ))}
            </List>
          </ScrollArea>
        </Grid.Col>
      </Grid>
    </>
  )
}

export default RouteStatistics
